import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { services, PHONE_DISPLAY, PHONE_TEL } from "../data/services";

const anchors = [
  { label: "Services", to: "/#services", id: "nav-services" },
  { label: "About", to: "/about", id: "nav-about" },
  { label: "Estimator", to: "/#calculator", id: "nav-estimator" },
  { label: "Fleet", to: "/#fleet", id: "nav-fleet" },
  { label: "Reviews", to: "/#reviews", id: "nav-reviews" },
  { label: "Contact", to: "/contact", id: "nav-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-blue-900/10"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 h-[96px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0" onClick={() => setOpen(false)}>
          <img
            src="/logo-full.png"
            alt="Way2Go Transport Co."
            data-testid="header-logo"
            className="h-[76px] w-auto"
            onError={(e) => {
              e.currentTarget.src = "/logo.jpg";
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav">
          <div className="relative group">
            <Link
              to="/#services"
              data-testid="nav-services"
              className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-navy hover:text-royal-bright transition-colors duration-200"
            >
              Services <ChevronDown size={14} className="mt-px" />
            </Link>
            <div className="absolute left-0 top-full pt-2 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-[opacity,transform] duration-200">
              <div className="w-72 bg-white border border-blue-900/10 shadow-[0_16px_48px_rgba(0,51,160,0.12)] py-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    className="flex items-center justify-between px-5 py-2.5 text-sm font-medium text-navy/80 hover:text-royal hover:bg-blue-50/70 transition-colors duration-150"
                  >
                    {s.name}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-40" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
          {anchors.slice(1).map((a) => (
            <Link
              key={a.id}
              to={a.to}
              data-testid={a.id}
              className="px-4 py-2 text-sm font-semibold text-navy hover:text-royal-bright transition-colors duration-200"
            >
              {a.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="header-phone-button"
            className="hidden md:flex items-center gap-2 px-4 h-11 font-mono text-sm font-bold text-royal border border-royal/25 hover:border-royal hover:bg-blue-50/60 transition-[background-color,border-color] duration-200"
          >
            <Phone size={15} strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-button"
            className="hidden sm:flex items-center h-11 px-5 bg-royal text-white text-sm font-bold tracking-wide shadow-[4px_4px_0px_#040B16] hover:shadow-[2px_2px_0px_#040B16] hover:translate-x-[2px] hover:translate-y-[2px] transition-[box-shadow,transform] duration-150"
          >
            GET A QUOTE
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="header-mobile-call-button"
            aria-label={`Call ${PHONE_DISPLAY}`}
            className="md:hidden flex items-center justify-center h-11 w-11 bg-royal text-white shadow-[3px_3px_0px_#040B16] active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-[box-shadow,transform] duration-150"
          >
            <Phone size={18} strokeWidth={2.5} />
          </a>
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-navy"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-blue-900/10"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`mobile-nav-${s.slug}`}
                  onClick={() => setOpen(false)}
                  className="py-3 font-display text-2xl font-bold text-navy border-b border-blue-900/10"
                >
                  {s.name}
                </Link>
              ))}
              <Link
                to="/about"
                data-testid="mobile-nav-about"
                onClick={() => setOpen(false)}
                className="py-3 font-display text-2xl font-bold text-navy border-b border-blue-900/10"
              >
                About
              </Link>
              <Link
                to="/contact"
                data-testid="mobile-nav-contact"
                onClick={() => setOpen(false)}
                className="py-3 font-display text-2xl font-bold text-navy border-b border-blue-900/10"
              >
                Contact
              </Link>
              <Link
                to="/quote"
                data-testid="mobile-quote-button"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center justify-center h-12 bg-royal text-white font-bold"
              >
                GET A QUOTE
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="mobile-phone-button"
                className="flex items-center justify-center gap-2 h-12 font-mono font-bold text-royal border border-royal/25"
              >
                <Phone size={16} /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
